import { HiPlusCircle } from 'react-icons/hi';

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  formatDate,
  ImageViewer,
  StatusField,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@amfintech/react-admin-ui';
import {
  CreateDataInput,
  createHelpers,
  createResource,
  sentenceCase,
  useResourceAccess,
} from '@amfintech/refine-react-admin';
import { useOne } from '@refinedev/core';

import {
  AccountType,
  ConsentType,
  ModificationEntityType,
  Profile,
  ProfileConsent,
  UserStatus,
  User,
  ProfileStatus,
} from '~/api';
import { ProfileConsentPage } from '~/pages/profile/profile-consent';
import { ProfileMaintenancePage } from '~/pages/profile/profile-maintenance';
import { ProfileTransactionsPage } from '~/pages/profile/profile-transactions';

import { resourceNames } from './resource-names';

const {
  defineFields,
  defineShowPage,
  defineCardSection,
  defineTabsSection,
  defineCustomSection,
  createTableComponent,
  createResourceEditDialog,
  createResourceCreateButton,
} = createHelpers<Profile>({
  resourceName: resourceNames.profile,
});

export const formatUserStatus = (status?: UserStatus) => {
  switch (status) {
    case UserStatus.Active:
      return 'success';
    case UserStatus.Inactive:
    case UserStatus.Dormant:
      return 'pending';
    case UserStatus.Closed:
      return 'danger';
    case UserStatus.ProfileLocked:
      return 'warn';
    default:
      return 'pending';
  }
};

export const formatProfileStatus = (status?: ProfileStatus) => {
  switch (status) {
    case ProfileStatus.Active:
      return 'success';
    case ProfileStatus.Suspended:
    case ProfileStatus.Legal:
    case ProfileStatus.Dormant:
      return 'warn';
    case ProfileStatus.Inactive:
      return 'pending';
    case ProfileStatus.Deceased:
    case ProfileStatus.Bankrupt:
    case ProfileStatus.Closed:
      return 'danger';
    default:
      return 'danger';
  }
};

const profileStatusOptions = [
  ProfileStatus.Active,
  ProfileStatus.Deceased,
  ProfileStatus.Suspended,
  ProfileStatus.Legal,
  ProfileStatus.Bankrupt,
  ProfileStatus.Inactive,
  ProfileStatus.Closed,
  ProfileStatus.Dormant,
].map((status) => ({ label: status, value: status }));

export const customerResource = createResource({
  identifier: resourceNames.customer,
  name: resourceNames.profile,
  label: 'Customer Profiles',
  fields: defineFields([
    'id',
    'type',
    'fullName',
    'salutation',
    'phoneNo',
    'nationality',
    'idType',
    'idNo',
    'idExpiryDate',
    'dateOfBirth',
    'cityOfBirth',
    'countryOfBirth',
    'gender',
    'bumiputera',
    'race',
    'maritalStatus',
    'religion',
    'mothersMaidenName',
    'email',
    {
      companyAddresses: [
        { mailingAddress: ['address', 'postalCode', 'state', 'town'] },
        { permanentAddress: ['address'] },
      ],
    },
    'employmentStatus',
    'occupation',
    'jobIndustry',
    'profession',
    'companyName',
    'natureOfBusiness',
    'natureOfBusinessDisplay',
    'officePhoneNo',
    'estimatedNetWorth',
    'annualIncome',
    'sourceOfFund',
    'sourceOfFundDisplay',
    'purposeOfInvestment',
    'expectedInvestmentAmount',
    'frequencyOfTransaction',
    'politicalExposedPerson',
    'prominentRole',
    'taxResidency',
    'declaration',
    'safRating',
    'marketingPush',
    'amlSanctionScreeningPoint',
    'employmentType',
    'monthlyIncome',
    'sourceOfWealth',
    'sourceOfWealthDisplay',
    'riskProfile',
    'riskProfileAttachmentId',
    'status',
    { users: ['id', 'phoneNo', 'hpPhoneNo', 'onboardedAt'] },
    { bankDetails: ['bankCode', 'beneficiaryName', 'accountNo', 'isDefault'] },
  ]),
  defaultValues: {},
  list: {
    filters: {
      required: true,
      collapsible: 'expand-by-default',
      initial: [
        {
          field: 'type',
          operator: 'eq',
          value: AccountType.Corporate,
        },
      ],
    },
    makerChecker: {
      resourceName: resourceNames.profile,
      entityTypes: [
        ModificationEntityType.User,
        ModificationEntityType.ProfileConsent,
        ModificationEntityType.AuthorisedSignatory,
        ModificationEntityType.Profile,
        ModificationEntityType.InvitationCode,
      ],
      historyDownloadConfig: {
        fields: [
          'referenceKeys',
          'entityType',
          'entityIdentifier',
          { modifyingUser: ['name'] },
          'modifiedBy',
          'modifiedAt',
          { reviewingUser: ['name'] },
          'reviewedAt',
          'type',
          'status',
        ],
        headers: {
          referenceKeys: 'Item',
          entityType: 'Entity Type',
          entityIdentifier: 'Identifier',
          'modifyingUser.name': 'Modified By',
          modifiedAt: 'Modified At',
          'reviewingUser.name': 'Reviewed By',
          reviewedAt: 'Reviewed At',
          type: 'Type',
          status: 'Status',
        },
        formatters: {
          referenceKeys: (value) => {
            if (!value || typeof value !== 'object') return '';
            return Object.values(value).join('-');
          },
          entityType: (value) => {
            if (value === ModificationEntityType.User) return 'Maintenance';
            return sentenceCase(value as string);
          },
          'modifyingUser.name': (value, item) => value || item.modifiedBy || 'SYSTEM',
          modifiedAt: (value) => formatDate(value, { formatType: 'dateAndTime' }),
          reviewedAt: (value) => (value ? formatDate(value, { formatType: 'dateAndTime' }) : ''),
        },
        filterConfig: {
          collapsible: 'expand-by-default',
        },
        filterControls: {
          modifiedAt: {
            type: 'daterange',
            config: {
              label: 'Modified Date',
            },
            operator: 'between',
          },
          entityType: {
            type: 'select',
            config: {
              label: 'Entity Type',
              options: [
                { label: 'User', value: ModificationEntityType.User },
                { label: 'Profile Consent', value: ModificationEntityType.ProfileConsent },
                { label: 'Authorised Signatory', value: ModificationEntityType.AuthorisedSignatory },
                { label: 'Profile', value: ModificationEntityType.Profile },
                { label: 'Invitation Code', value: ModificationEntityType.InvitationCode },
              ],
            },
            operator: 'eq',
          },
        },
      },
    },
  },
  filterControls: {
    idNo: {
      type: 'text',
      config: {
        label: 'ID Number',
      },
      operator: 'contains',
    },
    companyName: {
      type: 'text',
      config: {
        label: 'Company Name',
      },
      operator: 'contains',
    },
    type: {
      type: 'select',
      config: {
        label: 'Account Type',
        options: [
          { label: 'Individual', value: AccountType.Individual },
          { label: 'Corporate', value: AccountType.Corporate },
        ],
      },
      operator: 'eq',
    },
    status: {
      type: 'select',
      config: {
        label: 'Status',
        options: profileStatusOptions,
      },
      operator: 'eq',
    },
  },
  allowCreate: false,
  defaultSorter: [{ field: 'createdAt', order: 'desc' }],
  columns: ({ LinkToDetails }) => [
    {
      id: 'companyName',
      header: 'Company Name',
      accessorKey: 'companyName',
      cell: (data) => {
        // return sentenceCase(data.cell.getValue<string>());
        const companyName = data.cell.getValue<string>();

        return (
          <LinkToDetails resourceId={data.row.original.id} className="font-semibold">
            {companyName}
          </LinkToDetails>
        );
      },
    },
    {
      id: 'idNo',
      header: 'ID Number',
      accessorKey: 'idNo',
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (data) => {
        const status = data.cell.getValue<ProfileStatus>();
        return <StatusField status={formatProfileStatus(status)} label={status} />;
      },
    },
    {
      id: 'earliestOnboardedAt',
      header: 'Onboarded Date',
      cell: ({ row }) => {
        const earliestOnboardedAt = row.original.users
          ?.map((user) => user.onboardedAt)
          .filter(Boolean)
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];

        return earliestOnboardedAt
          ? formatDate(earliestOnboardedAt, { formatType: 'dateOnly' })
          : '';
      },
    },
  ],
  allowEdit: false,
  allowDelete: false,
  show: defineShowPage({
    extras: ({ currentTab, data }) => {
      const { canWrite } = useResourceAccess(resourceNames.profile);
      const ConsentCreateButton = createResourceCreateButton<
        ProfileConsent,
        CreateDataInput<ProfileConsent>
      >({
        resourceName: resourceNames.profileConsent,
        resourceLabel: 'Profile Consent',
        confirmBeforeSubmit: true,
        confirmationTitle: 'Profile Consent Confirmation',
        confirmationDescription: 'Please confirm that you want to create this Profile Consent.',
        defaultValues: {
          recipientId: data?.idNo,
        },
        controls: {
          components: {
            recipientId: {
              type: 'hidden',
            },
            issuerId: {
              type: 'text',
              config: {
                label: 'Company ID Number',
                placeholder: 'Enter company ID number',
                required: true,
                maxLength: 191,
              },
            },
            approvedEmail: {
              type: 'text',
              config: {
                label: 'Dedicated Email Address',
                placeholder: 'Enter dedicated email address',
                required: true,
                maxLength: 191,
              },
            },
            consentType: {
              type: 'select',
              config: {
                label: 'Consent Type',
                placeholder: 'Select consent type',
                options: Object.values(ConsentType).map((consentType) => ({
                  label: consentType,
                  value: consentType,
                })),
                required: true,
              },
            },
            attachment: {
              type: 'image-upload',
              config: {
                label: 'Attachment',
                required: true,
              },
            },
          },
        },
      });

      const MaintenanceCreateButton = createResourceCreateButton<User, CreateDataInput<User>>({
        resourceName: resourceNames.user,
        resourceLabel: 'New User',
        confirmBeforeSubmit: true,
        confirmationTitle: 'Maintenance User Confirmation',
        confirmationDescription: 'Please confirm that you want to create this maintenance user.',
        defaultValues: {
          ['profiles.connect.0.id']: data?.id,
          status: UserStatus.Inactive,
        },
        controls: {
          components: {
            name: {
              type: 'text',
              config: {
                label: 'Full Name',
                placeholder: 'Enter full name',
                required: true,
                maxLength: 191,
              },
            },
            email: {
              type: 'text',
              config: {
                label: 'Email Address',
                placeholder: 'Enter email address',
                required: true,
                type: 'email',
                maxLength: 191,
              },
            },
            phoneNo: {
              type: 'text',
              config: {
                label: 'Office Phone No',
                placeholder: 'Enter office phone number',
                pattern: '^[0-9]+$',
                patternMessage: 'Only digits are allowed',
                maxLength: 191,
              },
            },
            hpPhoneNo: {
              type: 'text',
              config: {
                label: 'HP Phone No',
                placeholder: 'Enter HP phone number',
                pattern: '^[0-9]+$',
                patternMessage: 'Only digits are allowed',
                maxLength: 191,
              },
            },
            status: {
              type: 'hidden',
            },
            ['profiles.connect.0.id']: {
              type: 'hidden',
            },
          },
        },
      });

      return (
        <>
          {currentTab.value === 'profile-consent' ? (
            <ConsentCreateButton
              disabled={!canWrite}
              variant="solid"
              size="sm"
              className="min-w-[7rem]"
              icon={<HiPlusCircle />}
            />
          ) : currentTab.value === 'maintenance' ? (
            <MaintenanceCreateButton
              disabled={!canWrite}
              variant="solid"
              size="sm"
              className="min-w-[7rem]"
              icon={<HiPlusCircle />}
            />
          ) : undefined}
        </>
      );
    },
    sections: [
      defineTabsSection({
        tabs: [
          {
            label: 'Personal Information',
            content: [
              defineCardSection({
                title: 'Company Details',
                fields: [
                  'companyName',
                  { accounts: ['masterAccountNo'] },
                  'businessCategoryDisplay',
                  'createdAt',
                  'idType',
                  'idNo',
                  {
                    companyAddresses: [
                      { mailingAddress: ['address', 'postalCode', 'state', 'town'] },
                      { permanentAddress: ['address'] },
                    ],
                  },
                ],
                displays: {
                  idType: {
                    label: 'ID Type',
                    render: (data) => sentenceCase(data.idType ?? '-'),
                  },
                  'accounts.masterAccountNo': {
                    label: 'Master account number(s)',
                    render: (data) =>
                      data.accounts.map((account) => account.masterAccountNo).join(', '),
                  },
                  'companyAddresses.mailingAddress.address': {
                    label: 'Mailing Address',
                    render: (data) => data.companyAddresses?.mailingAddress?.address ?? '-',
                  },
                  'companyAddresses.permanentAddress.address': {
                    label: 'Permanent Address',
                    render: (data) => data.companyAddresses?.permanentAddress?.address ?? '-',
                  },
                  'companyAddresses.mailingAddress.town': {
                    label: 'City',
                    render: (data) => data.companyAddresses?.mailingAddress?.town ?? '-',
                  },
                },
                collapsible: 'expand-by-default',
              }),
              defineCardSection({
                title: 'Investment Account Details',
                fields: [
                  'marketingPush',
                  'highNetWorth',
                  'phoneNo',
                  'officePhoneNo',
                  'email',
                  'natureOfBusinessDisplay',
                  'sourceOfFundDisplay',
                  'sourceOfWealthDisplay',
                ],
                displays: {
                  marketingPush: {
                    label: 'Marketing Push',
                    render: (data) => <Switch checked={data.marketingPush === true} disabled />,
                  },
                  highNetWorth: {
                    label: 'High Net Worth',
                    render: (data) => <Switch checked={data.highNetWorth === true} disabled />,
                  },
                  natureOfBusinessDisplay: {
                    label: 'Nature of Business',
                    render: (data) => data.natureOfBusinessDisplay,
                  },
                  sourceOfFundDisplay: {
                    label: 'Source of Fund',
                    render: (data) => data.sourceOfFundDisplay,
                  },
                  sourceOfWealthDisplay: {
                    label: 'Source of Wealth',
                    render: (data) => data.sourceOfWealthDisplay,
                  },
                },
                collapsible: 'expand-by-default',
              }),
              defineCardSection({
                title: 'Risk Profile',
                fields: ['riskProfile', 'riskProfileAttachmentId'],
                displays: {
                  riskProfile: {
                    label: 'Risk Profile',
                    render: (data, modifications) => {
                      if (!modifications) {
                        return data.riskProfile;
                      }

                      return (
                        <modifications.ModificationDiff
                          oldValue={modifications.oldValues?.riskProfile}
                          newValue={modifications.newValues?.riskProfile}
                        />
                      );
                    },
                  },
                  riskProfileAttachmentId: {
                    span: 2,
                    label: '',
                    render: (data, modifications) => {
                      const ImageComponent = ({ id, alt }: { id?: string | null; alt: string }) => {
                        if (!id) {
                          return <p className="text-sm text-gray-500">No Attachment</p>;
                        }

                        const { data: imageData } = useOne({
                          resource: resourceNames.document,
                          id,
                          meta: { fields: ['encodedData'] },
                        });
                        return imageData?.data ? (
                          <ImageViewer
                            src={imageData.data.encodedData}
                            alt={alt}
                            className="max-w-full max-h-[800px] object-contain rounded-md"
                          />
                        ) : (
                          <p className="text-sm text-gray-500">No Attachment</p>
                        );
                      };

                      if (!modifications) {
                        return (
                          <div>
                            <p className="font-semibold mb-2"> Attachment:</p>
                            <ImageComponent
                              id={data.riskProfileAttachmentId}
                              alt="Attachment image"
                            />
                          </div>
                        );
                      }

                      return (
                        <div className="flex flex-col gap-4 sm:flex-row sm:gap-8">
                          <div>
                            <p className="font-semibold mb-2">New Attachment:</p>
                            <ImageComponent
                              id={modifications.newValues.riskProfileAttachmentId}
                              alt="New attachment image"
                            />
                          </div>
                          <div>
                            <p className="font-semibold mb-2">Old Attachment:</p>
                            <ImageComponent
                              id={modifications.oldValues.riskProfileAttachmentId}
                              alt="Old attachment image"
                            />
                          </div>
                        </div>
                      );
                    },
                  },
                },
                collapsible: 'expand-by-default',
              }),
              defineCustomSection({
                fields: [
                  {
                    bankDetails: ['bankCode', 'beneficiaryName', 'accountNo', 'isDefault'],
                  },
                ],
                render: ({ queryResult }) => {
                  const bankDetails = queryResult?.data?.data?.bankDetails || [];

                  return (
                    <Card>
                      <CardHeader bordered>
                        <CardTitle>Bank Details</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="rounded-md border mb-4 mt-4">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Bank Code</TableHead>
                                <TableHead>Beneficiary Name</TableHead>
                                <TableHead>Account Number</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {bankDetails.map((account, index) => (
                                <TableRow key={index}>
                                  <TableCell>{account.bankCode || '-'}</TableCell>
                                  <TableCell>{account.beneficiaryName || '-'}</TableCell>
                                  <TableCell>{account.accountNo || '-'}</TableCell>
                                </TableRow>
                              ))}
                              {bankDetails.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={4} className="text-center py-4">
                                    No bank accounts found
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  );
                },
              }),
            ],
          },
          // {
          //   label: 'Transactions',
          //   content: [
          //     defineCustomSection({
          //       fields: ['id'],
          //       render: ({ queryResult }) => (
          //         <ProfileTransactionsPage
          //           createTableComponent={createTableComponent}
          //           profileId={queryResult?.data?.data?.id}
          //         />
          //       ),
          //     }),
          //   ],
          // },
          {
            label: 'Profile Consent',
            content: [
              defineCustomSection({
                fields: ['id'],
                render: ({ queryResult }) => {
                  const profileId = queryResult?.data?.data?.id;
                  if (!profileId) {
                    return <></>;
                  }
                  return (
                    <ProfileConsentPage
                      createTableComponent={createTableComponent}
                      profileId={profileId}
                    />
                  );
                },
              }),
            ],
          },
          {
            label: 'Maintenance',
            content: [
              defineCustomSection({
                fields: ['id'],
                render: ({ queryResult }) => {
                  const profileId = queryResult?.data?.data?.id;
                  if (!profileId) {
                    return <></>;
                  }
                  return (
                    <ProfileMaintenancePage
                      createTableComponent={createTableComponent}
                      createResourceEditDialog={createResourceEditDialog}
                      profileId={profileId}
                    />
                  );
                },
              }),
            ],
          },
        ],
      }),
    ],
  }),
});
